import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../../config";
import Sidebar from "./Sidebar";
import AssessmentTopNav from "./topnav";
import axios from "axios";
import "../../../Styles/Admin/assessment/Companies.css";

import "../../../axios_overrite";

// Base domain used to build the full company URL automatically.
// Change this if your production domain changes.
const BASE_DOMAIN = "https://outstandingworkplaces.co";

const CURRENT_YEAR = new Date().getFullYear();
const PAGE_SIZE = 20;

const emptyCompanyForm = {
  id: null,
  name: "",
  status: 1,
  employees_count: 0,
  company_url: "",
  company_full_url: "",
  year: "",
  username: "",
};

// Builds the page-number list for pagination, collapsing long runs into
// an ellipsis, e.g. [1, '...', 4, 5, 6, '...', 25]
const getPageNumbers = (current, total) => {
  if (total <= 1) return [1];
  const delta = 1;
  const range = [];
  for (
    let i = Math.max(2, current - delta);
    i <= Math.min(total - 1, current + delta);
    i++
  ) {
    range.push(i);
  }
  if (current - delta > 2) range.unshift("...");
  if (current + delta < total - 1) range.push("...");
  range.unshift(1);
  if (total > 1) range.push(total);
  return range;
};

const AddCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState("all");
  const [yearOptions, setYearOptions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [yearFilterOpen, setYearFilterOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [modalCompany, setModalCompany] = useState(emptyCompanyForm);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteCompanyId, setDeleteCompanyId] = useState(null);

  const [successAlert, setSuccessAlert] = useState("");
  const [errorAlert, setErrorAlert] = useState("");

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusCompany, setStatusCompany] = useState(null);
  const [statusAction, setStatusAction] = useState(""); // "activate" or "deactivate"

  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Builds a continuous list of years for the dropdown: any year present in
  // the data, plus the current year, plus a few years ahead for new
  // companies being onboarded — with no gaps in between.
  const computeYearOptions = (companiesList) => {
  const yearsFromData = companiesList
    .map((c) => parseInt(c.year, 10))
    .filter((y) => Number.isFinite(y));
  // Only unique years that actually exist in the data, sorted descending (newest first)
  const uniqueYears = [...new Set(yearsFromData)].sort((a, b) => b - a);
  return uniqueYears;
};

  const applyFilters = (companiesList, searchValue, yearValue) => {
    return companiesList.filter((c) => {
      const matchesSearch = c.name
        .toLowerCase()
        .includes(searchValue.toLowerCase());
      const matchesYear =
        yearValue === "all" || String(c.year) === String(yearValue);
      return matchesSearch && matchesYear;
    });
  };

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/assessment/companies/list`
      );
      setCompanies(res.data);
      setYearOptions(computeYearOptions(res.data));
      setFiltered(applyFilters(res.data, search, selectedYear));
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setFiltered(applyFilters(companies, value, selectedYear));
    setCurrentPage(1);
  };

  const selectYear = (value) => {
    setSelectedYear(value);
    setFiltered(applyFilters(companies, search, value));
    setCurrentPage(1);
    setYearFilterOpen(false);
  };

  const openAddModal = () => {
    setModalType("add");
    setModalCompany(emptyCompanyForm);
    setModalOpen(true);
  };

  const openEditModal = (company) => {
    setModalType("edit");
    setModalCompany({
      id: company.id,
      name: company.name,
      status: company.status,
      employees_count: company.employees_count || 0,
      company_url: company.company_url || "",
      company_full_url: company.company_full_url || "",
      year: company.year || CURRENT_YEAR,
      username: company.username || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalCompany(emptyCompanyForm);
  };

  // Builds the full URL from a path, always prefixing with a single "/"
  const buildFullUrl = (path) => {
    if (!path) return "";
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${BASE_DOMAIN}${cleanPath}`;
  };

  const handleModalChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Whenever company_url changes, auto-update the generated full URL.
    // (company_full_url itself is now editable, so the user can still
    // manually override it afterward.)
    if (name === "company_url") {
      const cleanPath = value && !value.startsWith("/") ? `/${value}` : value;
      setModalCompany((prev) => ({
        ...prev,
        company_url: cleanPath,
        company_full_url: buildFullUrl(cleanPath),
      }));
      return;
    }

    setModalCompany((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
            ? 1
            : 0
          : name === "employees_count"
          ? parseInt(value) || 0
          : value,
    }));
  };

  const handleModalSave = async () => {
    if (!modalCompany.name.trim()) return;
    try {
      const payload = {
        name: modalCompany.name,
        status: modalCompany.status,
        employees_count: modalCompany.employees_count,
        company_url: modalCompany.company_url,
        company_full_url: modalCompany.company_full_url,
        year: modalCompany.year
          ? parseInt(modalCompany.year) || CURRENT_YEAR
          : CURRENT_YEAR,
        username: modalCompany.username,
      };
      if (modalType === "add") {
        await axios.post(
          `${API_BASE_URL}/api/assessment/companies/add`,
          payload
        );
        setSuccessAlert("Company added successfully!");
      } else {
        await axios.post(
          `${API_BASE_URL}/api/assessment/companies/edit/${modalCompany.id}`,
          payload
        );
        setSuccessAlert("Company updated successfully!");
      }
      closeModal();
      fetchCompanies();
      setTimeout(() => setSuccessAlert(""), 2000);
    } catch (err) {
      setErrorAlert("Error saving company!");
      setTimeout(() => setErrorAlert(""), 2000);
      console.error(err);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteCompanyId(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteCompanyId(null);
    setDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/assessment/companies/delete/${deleteCompanyId}`
      );
      closeDeleteModal();
      fetchCompanies();
      setErrorAlert("Company deleted successfully!");
      setTimeout(() => setErrorAlert(""), 2000);
    } catch (err) {
      setErrorAlert("Error deleting company!");
      setTimeout(() => setErrorAlert(""), 2000);
      console.error(err);
    }
  };

  const openStatusModal = (company, action) => {
    setStatusCompany(company);
    setStatusAction(action); // "activate" or "deactivate"
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setStatusCompany(null);
    setStatusAction("");
    setStatusModalOpen(false);
  };

  const confirmStatusChange = async () => {
    if (!statusCompany) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/assessment/companies/edit/${statusCompany.id}`,
        {
          name: statusCompany.name,
          status: statusAction === "activate" ? 1 : 0,
          employees_count: statusCompany.employees_count,
          company_url: statusCompany.company_url,
          company_full_url: statusCompany.company_full_url,
          year: statusCompany.year,
          username: statusCompany.username,
        }
      );
      setSuccessAlert(
        statusAction === "activate"
          ? "Company activated successfully!"
          : "Company deactivated successfully!"
      );
      closeStatusModal();
      fetchCompanies();
      setTimeout(() => setSuccessAlert(""), 2000);
    } catch (err) {
      setErrorAlert("Error updating company status!");
      setTimeout(() => setErrorAlert(""), 2000);
      closeStatusModal();
      console.error(err);
    }
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = filtered.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(safePage * PAGE_SIZE, filtered.length);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pageNumbers = getPageNumbers(safePage, totalPages);

   return (
    <div className="companies-layout">
      <Sidebar />
      <div className="companies-body">
        <AssessmentTopNav title="CLIENTS" />
        <main className="companies-main">
          <header className="companies-header">
            <h1 className="companies-title">Companies</h1>
            <p className="companies-subtitle">
              View and manage all companies and their assessment progress
            </p>
          </header>

          <section className="companies-toolbar-card">
            <div className="companies-search-wrap">
              <img
                src="/searchs_icon.png"
                alt=""
                className="companies-search-icon"
              />
              <input
                type="text"
                placeholder="Search Company"
                value={search}
                onChange={handleSearch}
                className="companies-search-input"
              />
            </div>

            <div className="companies-year-wrap">
              <button
                type="button"
                className="companies-year-btn"
                onClick={() => setYearFilterOpen((open) => !open)}
                aria-label="Filter by year"
              >
                <img
                  src="/companies_cal_icons.png"
                  alt=""
                  className="companies-year-icon"
                />
                <span>{selectedYear === "all" ? "All Years" : selectedYear}</span>
                <img
                  src="/drop_downs_icons.png"
                  alt=""
                  className="companies-year-chevron"
                />
              </button>
              {yearFilterOpen && (
                <ul className="companies-year-dropdown">
                  <li>
                    <button
                      type="button"
                      className="companies-year-option"
                      onClick={() => selectYear("all")}
                    >
                      All Years
                    </button>
                  </li>
                  {yearOptions.map((y) => (
                    <li key={y}>
                      <button
                        type="button"
                        className="companies-year-option"
                        onClick={() => selectYear(y)}
                      >
                        {y}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button className="companies-add-btn" onClick={openAddModal}>
              <img
                src="/plus_icons.png"
                alt=""
                className="companies-plus-icon"
              />
              Add New Company
            </button>
          </section>

          {successAlert && (
            <div className="companies-success-alert">{successAlert}</div>
          )}
          {errorAlert && (
            <div className="companies-error-alert">{errorAlert}</div>
          )}

          {loading ? (
            <section className="companies-status-card">
              <img
                src="/company_ass_bg.png"
                alt=""
                className="companies-status-img"
              />
              <div className="companies-status-title">
                Loading Companies…
              </div>
              <div className="companies-status-desc">
                Please wait while we fetch your data
              </div>
            </section>
          ) : filtered.length === 0 ? (
            <section className="companies-status-card">
              <img
                src="/company_ass_bg.png"
                alt=""
                className="companies-status-img"
              />
              <div className="companies-status-title">Companies Report</div>
              <div className="companies-status-desc">
                Select the filters to view all companies and their assessment
                progress
              </div>
            </section>
          ) : (
            <>
              <section className="companies-table-card">
                <div className="companies-table-scroll">
                  <table className="companies-table">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Company</th>
                        <th>Assessment Year</th>
                        <th>User Name</th>
                        <th>Company Full URL</th>
                        <th>Employees</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((company, idx) => (
                        <tr key={company.id}>
                          <td>
                            {String(
                              (safePage - 1) * PAGE_SIZE + idx + 1
                            ).padStart(2, "0")}
                          </td>
                          <td className="companies-name-cell">
                            {company.name}
                          </td>
                          <td>{company.year || "-"}</td>
                          <td>{company.username || "-"}</td>
                          <td className="companies-url-cell">
                            {company.company_full_url || "-"}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="companies-employees-link"
                              onClick={() =>
                                navigate(
                                  `/admin/assessment/employees?companyId=${company.id}`
                                )
                              }
                            >
                              {company.employees_count || 0}
                            </button>
                          </td>
                          <td>
                            <div className="companies-status-cell">
                              <span
                                className={`companies-status-dot ${
                                  company.status === 1 ? "active" : "inactive"
                                }`}
                              />
                              <span className="companies-status-label">
                                {company.status === 1 ? "Active" : "Inactive"}
                              </span>
                              <label className="companies-toggle">
                                <input
                                  type="checkbox"
                                  checked={company.status === 1}
                                  onChange={() =>
                                    openStatusModal(
                                      company,
                                      company.status === 1
                                        ? "deactivate"
                                        : "activate"
                                    )
                                  }
                                />
                                <span className="companies-toggle-slider" />
                              </label>
                            </div>
                          </td>
                          <td>
                            <div className="companies-actions-cell">
                              <button
                                className="companies-edit-btn"
                                onClick={() => openEditModal(company)}
                              >
                                <img
                                  src="/edits_out_icon.png"
                                  alt=""
                                  className="companies-btn-icon"
                                />
                                Edit
                              </button>
                              <button
                                className="companies-delete-btn"
                                onClick={() => openDeleteModal(company.id)}
                              >
                                <img
                                  src="/deletes_out_icon.png"
                                  alt=""
                                  className="companies-btn-icon"
                                />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <div className="companies-pagination">
                <span className="companies-pagination-info">
                  Showing {pageStart}-{pageEnd} of {filtered.length} Companies
                </span>
                <div className="companies-pagination-controls">
                  <button
                    type="button"
                    className="companies-page-btn companies-page-nav"
                    disabled={safePage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    &#8249;
                  </button>
                  {pageNumbers.map((p, i) =>
                    p === "..." ? (
                      <span
                        key={`ellipsis-${i}`}
                        className="companies-page-ellipsis"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        className={`companies-page-btn ${
                          p === safePage ? "active" : ""
                        }`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="companies-page-btn companies-page-nav"
                    disabled={safePage === totalPages}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    aria-label="Next page"
                  >
                    &#8250;
                  </button>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="companies-modal-overlay">
          <div className="companies-modal">
            <div className="companies-modal-title">
              {modalType === "add" ? "Add New Company Details" : "Edit Company Details"}
            </div>
            <div className="companies-modal-subtitle">
              Update Company Information And Assessment Details
            </div>

            <label className="companies-modal-label" htmlFor="name">
              Company Name
            </label>
            <input
              className="companies-modal-input"
              id="name"
              name="name"
              value={modalCompany.name}
              onChange={handleModalChange}
              placeholder="Enter the company name"
              autoFocus
            />

            <label className="companies-modal-label" htmlFor="year">
              Assessment Year
            </label>
            <input
              className="companies-modal-input"
              id="year"
              name="year"
              type="text"
              inputMode="numeric"
              value={modalCompany.year}
              onChange={handleModalChange}
              placeholder="Enter the year"
            />

            <label className="companies-modal-label" htmlFor="username">
              User Email ID
            </label>
            <input
              className="companies-modal-input"
              id="username"
              name="username"
              value={modalCompany.username}
              onChange={handleModalChange}
              placeholder="Enter User Email ID"
            />

            <label className="companies-modal-label" htmlFor="company_url">
              Company URL
            </label>
            <input
              className="companies-modal-input"
              id="company_url"
              name="company_url"
              value={modalCompany.company_url}
              onChange={handleModalChange}
              placeholder="/assessment/company-name/url"
            />

            <label
              className="companies-modal-label"
              htmlFor="company_full_url"
            >
              Generated Full URL
            </label>
            <input
              className="companies-modal-input"
              id="company_full_url"
              name="company_full_url"
              value={modalCompany.company_full_url}
              onChange={handleModalChange}
              placeholder="Enter the Generated Full URL"
            />

            <label
              className="companies-modal-label"
              htmlFor="employees_count"
            >
              Number of Employees
            </label>
            <input
              className="companies-modal-input"
              id="employees_count"
              name="employees_count"
              type="number"
              min="0"
              value={modalCompany.employees_count}
              onChange={handleModalChange}
              placeholder="Enter the Employees Count"
            />

            <div className="companies-modal-status-row">
              <span
                className={`companies-status-dot ${
                  modalCompany.status === 1 ? "active" : "inactive"
                }`}
              />
              <span className="companies-modal-status-label">Active</span>
              <label className="companies-toggle">
                <input
                  type="checkbox"
                  name="status"
                  checked={modalCompany.status === 1}
                  onChange={handleModalChange}
                />
                <span className="companies-toggle-slider" />
              </label>
            </div>

            <div className="companies-modal-actions">
              <button className="companies-modal-cancel" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="companies-modal-save"
                onClick={handleModalSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="companies-delete-modal-overlay">
          <div className="companies-delete-modal">
            <div className="delete-icon">
              <img
                src="/delete_alert_icons.png"
                alt=""
                className="delete-icon-img"
              />
            </div>
            <div className="companies-delete-modal-title">
              Delete Information
            </div>
            <div className="companies-delete-modal-desc">
              This Record will be deleted
            </div>
            <div className="companies-delete-modal-actions">
              <button
                className="companies-delete-modal-yes"
                onClick={confirmDelete}
              >
                Yes, Delete
              </button>
              <button
                className="companies-delete-modal-no"
                onClick={closeDeleteModal}
              >
                No, Keep it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {statusModalOpen && (
        <div className="companies-delete-modal-overlay">
          <div className="companies-delete-modal">
            <div className="delete-icon">
              <svg width="48" height="48" fill="none" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="24" fill="#FFF3ED" />
                <path
                  d="M24 14v12"
                  stroke="#FF5A1F"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <circle cx="24" cy="32" r="1.5" fill="#FF5A1F" />
              </svg>
            </div>
            <div className="companies-delete-modal-title">
              {statusAction === "activate" ? "Activate" : "Deactivate"}
            </div>
            <div className="companies-delete-modal-desc">
              Are you sure you want to {statusAction} this company?
            </div>
            <div className="companies-delete-modal-actions">
              <button
                className="companies-status-yes-btn"
                onClick={confirmStatusChange}
              >
                Yes
              </button>
              <button
                className="companies-status-no-btn"
                onClick={closeStatusModal}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCompanies;